import React from 'react';

import type { Theme } from '@mui/material/styles';
import type { GridProps } from '@mui/material';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';

import { IntlMessage } from 'app/components/Intl';
import RegulationsFieldsPanelRow from './RegulationsFieldsPanelRow';
import type { RegulationsPanelColumn } from '../Fields/RegulationsField.types';

import {
  DivHeaderContentStyled,
  GridColumnStyled,
  GridHeaderStyled,
  PaperBackgroundStyled,
  TypographyColumnSubTitleStyled,
  TypographyColumnTitleStyled,
} from './RegulationsFieldsPanel.styles';

export type RegulationsFieldsPanelProps = {
  header?: string | React.ReactNode;
  headerButton?: React.ReactNode;
  columns: Array<RegulationsPanelColumn>;
  loading?: boolean;
  disabled?: boolean;
  divider?: boolean;
};

const RegulationsFieldsPanel = ({
  header,
  headerButton,
  columns,
  loading = false,
  disabled = false,
  divider = false,
}: RegulationsFieldsPanelProps) => {
  const { breakpoints } = useTheme<Theme>();
  const smallScreen = !useMediaQuery(breakpoints.up('md'));

  const renderColumn = React.useCallback(
    (column: RegulationsPanelColumn, index: number) => {
      const columnsLength = Math.min(columns.length, 2);
      const columnWidth = Math.floor(12 / columnsLength);
      const columnsPerRow = Math.floor(12 / columnWidth);
      const hasDividerLeft = divider && !smallScreen && index % columnsPerRow !== 0;
      const hasDividerRight = divider && !smallScreen && index % columnsPerRow === 0;
      const hasDividerTop = divider && smallScreen && index !== 0 && columns.length > 1;
      const hasDividerBottom = divider && smallScreen && index === 0 && columns.length > 1;

      return (
        <GridColumnStyled
          key={column.id ?? index}
          item
          md={columnWidth as GridProps['md']}
          xs={12}
          hasDividerLeft={hasDividerLeft}
          hasDividerRight={hasDividerRight}
          hasDividerTop={hasDividerTop}
          hasDividerBottom={hasDividerBottom}
        >
          {column.title && (
            <TypographyColumnTitleStyled
              variant={'subtitle2'}
              withMarginTop={!!header}
              withMarginBottom={!column.subtitle}
            >
              <IntlMessage value={column.title} />
            </TypographyColumnTitleStyled>
          )}
          {column.subtitle && (
            <TypographyColumnSubTitleStyled variant={'body1'} withMarginTop={!column.title}>
              <IntlMessage value={column.subtitle} />
            </TypographyColumnSubTitleStyled>
          )}
          <RegulationsFieldsPanelRow loading={loading} rows={column.rows} disabled={disabled} />
        </GridColumnStyled>
      );
    },
    [columns, disabled, divider, header, loading, smallScreen],
  );

  return (
    <PaperBackgroundStyled>
      <Grid container spacing={2} direction={'column'} justifyContent={'space-between'} alignItems={'stretch'}>
        {header && (
          <GridHeaderStyled item container xs={'auto'}>
            <Grid item xs={true}>
              <DivHeaderContentStyled withIcon={!!headerButton}>
                <Typography variant={'overline'}>{header}</Typography>
                {!!headerButton && headerButton}
              </DivHeaderContentStyled>
            </Grid>
          </GridHeaderStyled>
        )}
        <Grid item container spacing={2}>
          {columns.map((column, index) => renderColumn(column, index))}
        </Grid>
      </Grid>
    </PaperBackgroundStyled>
  );
};

export default React.memo(RegulationsFieldsPanel);
